import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Component for testing dialog accessibility and responsive behavior
 * Tests various dialog configurations and content types
 */
export function DialogAccessibilityTest() {
  // State for the various dialog types
  const [standardOpen, setStandardOpen] = useState(false);
  const [titlelessOpen, setTitlelessOpen] = useState(false);
  const [largeContentOpen, setLargeContentOpen] = useState(false);
  const [screenSizeInfo, setScreenSizeInfo] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  // Monitor screen size for responsive testing
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setScreenSizeInfo({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="p-6 space-y-8">
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Dialog Accessibility and Responsive Testing</h1>
        <p className="text-muted-foreground">
          This component tests the accessibility and responsive behavior of dialogs
        </p>
        <div className="text-sm p-4 bg-muted rounded-md">
          <p>Current viewport: {screenSizeInfo.width}px × {screenSizeInfo.height}px</p>
          <p>Size category: {
            screenSizeInfo.width < 640 ? 'Mobile' :
            screenSizeInfo.width < 1024 ? 'Tablet' : 'Desktop'
          }</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <Button onClick={() => setStandardOpen(true)}>
          Open Standard Dialog
        </Button>
        <Button onClick={() => setTitlelessOpen(true)} variant="outline">
          Open Dialog without Title
        </Button>
        <Button onClick={() => setLargeContentOpen(true)} variant="secondary">
          Open Dialog with Large Content
        </Button>
      </div>

      {/* Standard dialog with proper accessibility structure */}
      <Dialog open={standardOpen} onOpenChange={setStandardOpen}>
        <DialogContent>
          <DialogTitle>Accessible Dialog Example</DialogTitle>
          <DialogHeader>
            <DialogDescription>
              This dialog follows best practices for accessibility with proper heading structure.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p>This dialog has:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Proper focus management</li>
              <li>Screen reader announcements</li>
              <li>Keyboard navigation</li>
              <li>Responsive sizing based on viewport</li>
              <li>ARIA attributes for accessibility</li>
            </ul>
          </div>
          <DialogFooter>
            <Button onClick={() => setStandardOpen(false)}>Close Dialog</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog without an explicit title (should add one for screen readers) */}
      <Dialog open={titlelessOpen} onOpenChange={setTitlelessOpen}>
        <DialogContent aria-label="Dialog without visible title">
          {/* No DialogTitle - the component should add a hidden one */}
          <div className="py-4">
            <p className="font-medium">This dialog intentionally lacks a DialogTitle component.</p>
            <p className="mt-2 text-muted-foreground">
              The enhanced dialog system should automatically add a screen-reader-only title
              to ensure accessibility, using the aria-label from the DialogContent.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setTitlelessOpen(false)}>Close Dialog</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog with large content to test responsive behavior */}
      <Dialog open={largeContentOpen} onOpenChange={setLargeContentOpen}>
        <DialogContent className="max-w-4xl">
          <DialogTitle>Large Content Dialog</DialogTitle>
          <DialogHeader>
            <DialogDescription>
              This dialog tests responsive handling of large content with proper scrolling.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-[50vh] overflow-y-auto pr-2">
            {/* Generate repeated content to test scrolling */}
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="mb-4 p-3 border rounded">
                <h3 className="font-medium">Content Section {i + 1}</h3>
                <p>This section tests the dialog's ability to handle long content with proper scrolling.</p>
                <p className="text-muted-foreground mt-2">
                  The dialog should maintain proper sizing and scrolling regardless of content length.
                </p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setLargeContentOpen(false)}>Close Dialog</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DialogAccessibilityTest;
